import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useSetAtom } from 'jotai';
import { openSnackbarAdviseAtom, userDataAtom } from '../../Atoms';
import LoginFormPage from './LoginFormPage';
import Verify2FA from './Verify2FA';
import ForgotPasswordPage from './ForgotPasswordPage';
import FirstLogin from './FirstLogin';
import useAuthAPI from '../../api/auth';
import NotAuthenticated from './NotAuthenticated';
import LandingPage from '../../Public/LandingPage';
import CallToActionPage from '../../Public/CallToActionPage';
import ResponseRegistered from '../../Public/ResponseRegistered';

const checkIfPasswordIsExpired = (FetchError) =>
  FetchError?.payload?.errors?.filter(
    (x) => typeof x === 'object' && x.code === 'PASSWORD_EXPIRED'
  ).length > 0;

const LoginRouter = function LoginRouter({ onLogin }) {
  const AuthAPI = useAuthAPI();
  const intl = useIntl();
  const navigate = useNavigate();
  const setUserData = useSetAtom(userDataAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const [loginError, setLoginError] = useState(null);
  const [otpErrorCount, setOtpErrorCount] = useState(0);
  const [needToChangePassword, setNeedToChangePassword] = useState(false);

  const handleOTPLogin = ({ otpCode, username }) => {
    AuthAPI.twoFactorLogin(
      otpCode,
      () => {
        setOtpErrorCount(0);
        setUserData({
          username,
        });
        onLogin();
      },
      () => {
        setOtpErrorCount((prev) => prev + 1);
        setOpenSnackbarAdvise({
          message: intl.messages.FAILED_AUTH,
          severity: 'error',
        });
      }
    );
  };
  const handleLogin = (data, captchaValue) => {
    if (data === 'passkey') {
      AuthAPI.fido2Authenticate(
        (result) => {
          setOpenSnackbarAdvise({
            message: intl.messages.SETUP_SUCCESS_2FA,
            severity: 'success',
          });
          setUserData({
            username: result.userName,
          });
          onLogin();
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.ERROR_SETUP_2FA,
            severity: 'error',
          });
        }
      );
    } else if (
      !('newPassword' in data) ||
      data.newPassword === data.confirmPassword
    ) {
      if (!captchaValue) {
        setLoginError(intl.messages.CAPTCHA_NOT_VALID);
        return;
      }
      AuthAPI.login(
        {
          username: data.username,
          password: data.password,
          captchaValue,
          newPassword: data?.newPassword,
        },
        (result) => {
          if (result.requiresTwoFactor) {
            setOtpErrorCount(0);
            navigate('/verify2FA', {
              state: { username: data.username, password: data.password },
            });
          } else {
            setUserData({
              username: data.username,
            });
            onLogin();
          }
        },
        (error) => {
          if (error?.payload) setLoginError(error);
          else
            setOpenSnackbarAdvise({
              message: intl.messages.LOGIN_ERROR,
              severity: 'error',
            });
        }
      );
    } else {
      setLoginError(intl.messages.PASSWORDS_MISMATCH);
    }
  };

  const handleExternalLogin = (provider) => {
    AuthAPI.externalLogin(
      provider,
      () => {},
      (error) => {
        setLoginError(error);
      }
    );
  };

  useEffect(() => {
    if (otpErrorCount >= 3) {
      setOpenSnackbarAdvise({
        message: intl.messages.FAILED_AUTH_TOO_MANY_FAILED_ATTEMPTS,
        severity: 'error',
      });
      navigate('/');
    }
  }, [otpErrorCount]);

  useEffect(() => {
    if (loginError && !needToChangePassword)
      setNeedToChangePassword(checkIfPasswordIsExpired(loginError));
  }, [loginError]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <LoginFormPage
            onSubmit={handleLogin}
            onExternalLogin={handleExternalLogin}
            needToChangePassword={needToChangePassword}
            widget={<FirstLogin loginError={loginError} />}
          />
        }
      />
      <Route
        path="/verify2FA"
        element={<Verify2FA onSubmit={handleOTPLogin} />}
      />
      <Route path="/forgotPassword" element={<ForgotPasswordPage />} />
      <Route path="/401" element={<NotAuthenticated />} />
      <Route
        path="/landingPage/:landingId/:activityId"
        element={<LandingPage />}
      />
      <Route
        path="/callToAction/:ctaId/:activityId"
        element={<CallToActionPage />}
      />
      <Route path="/responseRegistered" element={<ResponseRegistered />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

LoginRouter.propTypes = {
  onLogin: PropTypes.func.isRequired,
};

export default LoginRouter;
